import React, {useEffect} from 'react';
import {BrowserRouter, Routes, Route} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import Main from '@modules/main/Main';
import Login from '@modules/login/Login';
import Register from '@modules/register/Register';
import ForgetPassword from '@modules/forgot-password/ForgotPassword';
import RecoverPassword from '@modules/recover-password/RecoverPassword';
import {useWindowSize} from '@app/hooks/useWindowSize';
import {calculateWindowSize} from '@app/utils/helpers';
import {useDispatch, useSelector} from 'react-redux';
import {setWindowSize} from '@app/store/reducers/ui';

import Dashboard from '@pages/Dashboard';
import Daily from '@pages/Daily/Daily';
import MyDailies from '@app/pages/MyDailies';
import AllLeaves from '@app/pages/leaves/AllLeaves';

import AllDocs from '@app/pages/docs/AllDocs';
import UpdateDoc from '@app/pages/docs/UpdateDoc';
import ViewDoc from '@app/pages/docs/ViewDoc';
import CreateDoc from '@app/pages/docs/CreateDoc';

import AllCases from '@app/pages/cases/AllCases';
import CreateCase from '@app/pages/cases/CreateCase';

import AllClients from '@app/pages/clients/AllClients';
import CreateClient from '@app/pages/clients/CreateClient';
import ViewClient from '@app/pages/clients/ViewClient';

import AllUsers from '@app/pages/users/AllUsers';
import CreateUser from '@app/pages/users/CreateUser';
import ViewUser from '@app/pages/users/ViewUser';

import GetFile from '@app/pages/GetFile';
import CreateProject from '@app/pages/projects/CreateProject';
import ViewProject from '@app/pages/projects/ViewProject';
import ListProjects from '@app/pages/projects/ListProjects';
import MyLeaves from '@pages/leaves/MyLeaves';
import MyLeave from '@app/pages/leaves/MyLeaves';
import Profile from '@pages/profile/Profile';
import TaskView from '@app/pages/tasks/TaskView';
import AllDailies from './pages/AllDailies';
import UpdateProject from './pages/projects/UpdateProject';
import ApplyLeave from './pages/leaves/ApplyLeave';
import UpdateClient from './pages/clients/UpdateClient';
import Case from './pages/cases/Case';
import UpdateCase from './pages/cases/UpdateCase';
import ViewActivity from './pages/cases/ViewActivity';
import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';
import UpdateUser from './pages/users/UpdateUser';
import Todos from './pages/todos/Todos';
import Archives from './pages/todos/Archives';
import ViewFile from './components/view-file/ViewFile';
import Tasks from './pages/tasks/Tasks';
import ViewUpdateTask from './pages/tasks/ViewUpdateTask';
import GetTaskFile from './components/getTaskFile/GetTaskFile';
import LeavePolicy from './pages/leaves/LeavePolicy';
// import Progress from './pages/tasks/Progess';

const App = () => {
  const windowSize = useWindowSize();
  const screenSize = useSelector((state: any) => state.ui.screenSize);
  const dispatch = useDispatch();

  useEffect(() => {
    const size = calculateWindowSize(windowSize.width);

    if (windowSize.width < 992) {
      document.getElementById('root').innerHTML =
        '<h1>Screen size is not supported</h1>';
      return;
    }

    if (screenSize !== size) {
      dispatch(setWindowSize(size));
    }
  }, [windowSize]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PublicRoute />}>
          <Route path="/login" element={<Login />} />
        </Route>
        {/* <Route path="/register" element={<PublicRoute />}>
          <Route path="/register" element={<Register />} />
        </Route> */}
        <Route path="/forgot-password" element={<PublicRoute />}>
          <Route path="/forgot-password" element={<ForgetPassword />} />
        </Route>
        <Route path="/recover-password" element={<PublicRoute />}>
          <Route path="/recover-password" element={<RecoverPassword />} />
        </Route>
        <Route path="/" element={<PrivateRoute />}>
          <Route path="/file/:fileName" element={<GetFile />} />
          <Route path="/" element={<Main />}>
            <Route path="/all-leaves" element={<AllLeaves />} />
            <Route path="/apply-leave" element={<ApplyLeave />} />
            <Route path="/leave-policy" element={<LeavePolicy />} />
            <Route path="/my-dailies" element={<MyDailies />} />
            <Route path="/all-dailies" element={<AllDailies />}></Route>

            {/* Documentation Routes */}
            <Route path="/alldocs" element={<AllDocs />} />
            <Route path="/view-doc/:docId" element={<ViewDoc />} />
            <Route path="/create-doc/:projectId" element={<CreateDoc />} />
            <Route path="/update-doc/:docId" element={<UpdateDoc />} />
            <Route path="/getDoc/:hash" element={<ViewFile />} />

            {/* Client Routes */}
            <Route path="/all-clients" element={<AllClients />} />
            <Route path="/create-client" element={<CreateClient />} />
            <Route path="/view-client/:id" element={<ViewClient />} />
            <Route path="/update-client/:id" element={<UpdateClient />} />

            {/* Todo's Routes */}
            <Route path="todos" element={<Todos />} />
            <Route path="/update-todo/:id" element={<Todos />} />
            <Route path="archives" element={<Archives />} />
            {/* User Routes */}
            <Route path="/all-users" element={<AllUsers />} />
            <Route path="/update/:id" element={<UpdateUser />} />
            <Route path="/create-user" element={<CreateUser />} />
            <Route path="/view-user/:id" element={<ViewUser />} />

            {/* Cases Routes */}
            <Route path="/all-cases" element={<AllCases />} />
            <Route path="/cases" element={<Case />} />
            <Route path="/create-case" element={<CreateCase />} />
            <Route path="/update-case/:id" element={<UpdateCase />} />
            <Route path="/view-activity/:id" element={<ViewActivity />} />
            <Route path="/my-leaves" element={<MyLeaves />} />
            <Route path="/create-project" element={<CreateProject />} />
            <Route path="/view-project/:projectId" element={<ViewProject />} />
            <Route
              path="/update-project/:projectId"
              element={<UpdateProject />}
            />
            <Route path="/projects" element={<ListProjects />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/daily" element={<Daily />} />
            <Route path="/" element={<Daily />} />

            {/* Tasks Routes */}
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/tasks/:projectId/:taskNo" element={<TaskView />} />
          </Route>
        </Route>
      </Routes>
      <ToastContainer
        autoClose={3000}
        draggable={false}
        position="top-right"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnHover
      />
    </BrowserRouter>
  );
};

export default App;
